/**
 * **다시 보기**: 끝난 판의 기록(`Replay` — 명부와 커맨드 로그)을 화면에서 다시 돌린다. 플레이한 사람이 청했다:
 * "리플레이 기능을 만들어줘. 각 부대를 선택해서 카메라 포커스 옮기는 기능, 속도 조절기능, 자유 카메라 드래그 기능."
 *
 * 시뮬레이션이 결정론이라 기록에는 화면이 없다 — 같은 명부에 같은 커맨드를 같은 틱에 넣으면 같은 판이 나온다
 * (`sim/replay.ts`의 `runReplay`, 서버 검증과 같은 길). 여기는 그것을 한 틱씩 끊어 돌리는 것뿐이다. 그래서 **뒤로는
 * 못 간다** — 되감으려면 처음부터 다시 돌린다(`main.ts`의 `openReplay`가 판을 새로 열고 그 틱까지 빨리 돌린다).
 *
 * 그림을 모르는 셈만 둔다(시험이 본다). 화면 쪽은 `ReplayLoop`와 `ui/replay-view.ts`.
 */
import { CommandLog } from '../sim/commands.ts';
import type { Command } from '../sim/commands.ts';
import { replayOver } from '../sim/replay.ts';
import type { Replay } from '../sim/replay.ts';
import { createBattle } from '../sim/scenario.ts';
import { Role } from '../sim/troops.ts';
import { TICK_HZ, Team } from '../sim/units.ts';
import type { World } from '../sim/world.ts';
import type { Renderer } from '../render/renderer.ts';

/** 기록을 한 틱씩 돌리는 것. `runReplay`와 같은 순서로 같은 일을 한다 — 끝까지 돌리면 체크섬이 같다(시험이 못 박는다). */
export class ReplayPlayer {
  readonly world: World;
  /** 기록된 틱 수. 판이 끝난 틱이다. */
  readonly total: number;
  private readonly log: CommandLog;
  private readonly config: Replay['config'];
  private readonly buf: Command[] = [];
  private ended = false;

  constructor(replay: Replay) {
    this.world = createBattle(replay.config);
    this.config = replay.config;
    this.log = CommandLog.fromJSON(replay.commands);
    this.total = replay.ticks;
  }

  get tick(): number {
    return this.world.tick;
  }

  /** 끝까지 돌았는가. 장수가 쓰러진 뒤의 퇴각이 끝나면(`World.fallOver`) 기록이 더 길어도 거기서 끝난다 — `runReplay`와 같다. */
  get done(): boolean {
    return this.ended || this.world.tick >= this.total;
  }

  /** 한 틱 돌린다. 이미 끝났으면 아무것도 하지 않고 false. */
  step(): boolean {
    if (this.done) return false;
    this.log.take(this.world.tick + 1, this.buf);
    this.world.step(this.buf);
    if (replayOver(this.world, this.config)) this.ended = true;
    return true;
  }

  /** 그 틱까지 그리지 않고 돌린다(앞으로만). */
  seek(tick: number): void {
    while (this.world.tick < tick && this.step());
  }
}

/** 카메라가 볼 것. `free`는 손으로 옮긴 자리에 선다. */
export type ReplayFocus = { kind: 'free' } | { kind: 'squad'; squad: number } | { kind: 'chief' };

/**
 * 그 대상에서 카메라가 따라갈 유닛(전장 색인). 없으면 -1(카메라는 그 자리에 선다).
 *
 * 부대는 지휘관(내 부대는 장수, 다른 부대는 동료)을 본다. 지휘관이 쓰러졌으면 그 부대에 남은 병사 가운데 색인이 가장
 * 앞선 사람 — 지휘관이 쓰러지는 순간 카메라가 멈춰 서면 그 부대가 그 뒤 어떻게 됐는지 볼 수 없다. 전장을 빠져나간
 * 병사(`exited`)는 살아 있어도 전장에 없으니 따라가지 않는다.
 */
export function focusUnit(w: World, focus: ReplayFocus): number {
  if (focus.kind === 'free') return -1;
  if (focus.kind === 'chief') {
    const c = w.chiefIdx;
    if (c >= 0 && w.alive[c] === 1) return c;
    // 적의 대장은 여럿이다(총대장과 부장, 적장 결전은 적장 셋과 부장) — 총대장이 쓰러졌으면 남은 대장을 본다.
    for (let i = 0; i < w.count; i++) {
      if (w.alive[i] === 1 && w.team[i] === Team.Enemy && w.role[i] === Role.Chief) return i;
    }
    return -1;
  }
  const c = w.sqCommander[focus.squad];
  if (c >= 0 && w.alive[c] === 1 && w.exited[c] === 0) return c;
  for (let i = 0; i < w.count; i++) {
    if (w.alive[i] === 1 && w.exited[i] === 0 && w.team[i] === Team.Player && w.squad[i] === focus.squad) return i;
  }
  return -1;
}

/** 고를 수 있는 속도(배). 느리게는 한 합을 뜯어 보는 데, 빠르게는 맞붙기 전을 넘기는 데 쓴다. */
export const REPLAY_SPEEDS: readonly number[] = [0.5, 1, 2, 4, 8];

/** 틱을 "분:초"로. */
export function clockText(ticks: number): string {
  const s = Math.floor(ticks / TICK_HZ);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const TICK_MS = 1000 / TICK_HZ;
/** 한 프레임에 돌릴 수 있는 틱의 상한(1배에서). 속도를 올리면 그만큼 는다. 탭을 숨겼다 돌아왔을 때 폭주를 막는다. */
const MAX_CATCHUP = 5;

export interface ReplayLoopHooks {
  /** 틱이 하나 돈 뒤마다(한 프레임에 여러 번일 수 있다). 소리가 판의 상태를 견주는 자리 — 프레임마다 보면 배속에서
   *  사이의 일을 놓친다. 되감기(`ReplayPlayer.seek`)는 이것을 부르지 않는다. */
  onStep?: () => void;
  onFrame?: () => void;
  /** 끝까지 돌았을 때 한 번. */
  onEnd?: () => void;
}

/**
 * 다시 보기의 프레임 루프. 싸움의 루프(`GameLoop`)와 같은 고정 타임스텝이되 입력을 받지 않고, 시간에 속도를 곱한다.
 * 세우면(`playing = false`) 그리기만 돈다 — 선 채로 카메라를 옮겨 둘러볼 수 있다.
 */
export class ReplayLoop {
  /** 배속. */
  speed = 1;
  playing = true;
  private accumulator = 0;
  private last = 0;
  private running = false;
  private endSeen = false;
  private readonly tickFn = (): void => this.frame();
  private readonly player: ReplayPlayer;
  private readonly renderer: Renderer;
  private readonly hooks: ReplayLoopHooks;

  constructor(player: ReplayPlayer, renderer: Renderer, hooks: ReplayLoopHooks = {}) {
    this.player = player;
    this.renderer = renderer;
    this.hooks = hooks;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.renderer.app.ticker.add(this.tickFn);
  }

  private frame(): void {
    const now = performance.now();
    let dt = now - this.last;
    this.last = now;
    if (dt > 250) dt = 250;
    const run = this.playing && !this.player.done;
    // 선 동안에는 걷기 그림도 세운다 — 안 세우면 제자리에서 발만 구른다(`Renderer.frozen`).
    this.renderer.frozen = !run;
    if (run) {
      this.accumulator += dt * this.speed;
      const cap = Math.ceil(MAX_CATCHUP * Math.max(1, this.speed));
      let steps = 0;
      while (this.accumulator >= TICK_MS && steps < cap && this.player.step()) {
        this.accumulator -= TICK_MS;
        steps++;
        this.hooks.onStep?.();
      }
      if (steps === cap || this.player.done) this.accumulator = Math.min(this.accumulator, TICK_MS);
    }
    // 끝난 판은 마지막 틱의 모습 그대로(보간 1), 세운 판은 세운 자리에서.
    this.renderer.render(this.player.done ? 1 : Math.min(1, this.accumulator / TICK_MS));
    this.hooks.onFrame?.();
    if (this.player.done && !this.endSeen) {
      this.endSeen = true;
      this.hooks.onEnd?.();
    }
  }
}
